import { useState, useEffect, useCallback, useRef } from 'react';
import { advertisersApi } from '../api/advertisers';

/**
 * Hook for fetching advertisers list with stats
 */
export function useAdvertisers(filters) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filtersKey = JSON.stringify(filters || {});
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await advertisersApi.getAll(filtersRef.current || {});
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for fetching a single advertiser with campaigns
 */
export function useAdvertiser(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await advertisersApi.getById(id);
      setData(result.data || result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for advertiser mutations (create, update, delete)
 */
export function useAdvertiserMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createAdvertiser = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await advertisersApi.create(data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAdvertiser = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await advertisersApi.update(id, data);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAdvertiser = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const result = await advertisersApi.delete(id);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    createAdvertiser,
    updateAdvertiser,
    deleteAdvertiser,
    loading,
    error,
  };
}

export default useAdvertisers;
